import { createStyles, FormControl, FormLabel, TextField, Theme, WithStyles, withStyles } from '@material-ui/core'
import moment from 'moment'
import * as R from 'ramda'
import React from 'react'

import { generateYearsArray, isIE, isSafari } from '../../services/utils'

interface DatePickerProps extends WithStyles<typeof styles> {
	required?: boolean
	fullSize?: boolean
	label?: string
	date?: Date | string
	onDateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
	noOfYears: number
	topYear?: number
	hideLabel?: boolean
	minDate?: string
	maxDate?: string
	style?: object
	rootStyle?: object
}

interface DatePickerState {
	year?: number
	month?: string
	day?: number
	dateError?: boolean
	localDate: Date | string
}

export interface TemporaryDate {
	day?: number | string
	month?: number | string
	year?: number | string
}

const DAYS = R.range(1, 32)

export const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

const setStateFromProps = (props: DatePickerProps) => {
	return {
		localDate: props.date || '',
	}
}

export class DatePicker extends React.Component<DatePickerProps, DatePickerState> {
	public readonly state: DatePickerState = setStateFromProps(this.props)

	private YEARS = generateYearsArray(this.props.noOfYears, this.props.topYear).reverse()

	handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		e.persist()
    this.setState((prevState: DatePickerState) => {
			return {
				...prevState,
				localDate: e?.target?.value || ''
			}
		})
		if (!isIE && !isSafari) {
			this.props.onDateChange(e)
		} else {
			this.setState({ dateError: false })
			const { name, value } = e.target
			const { onDateChange } = this.props

			this.setState((prevState: DatePickerState) => {
				let newState = {
					...prevState,
					[name]: value,
				}
				const month = newState.month ? MONTHS.indexOf(newState.month) + 1 : newState.month
				const date = new Date(`${newState.year}/${month}/${newState.day}`)

				if (moment(date).isValid()) {
					const isDateNOTValid = this.isDateOutofBoundaries(date)
					if (isDateNOTValid) {
						newState = {
							month: undefined,
							day: undefined,
							year: undefined,
							localDate: '',
							dateError: true,
						}
					} else {
						const newEvent = Object.assign({}, e, { target: { value: date.toISOString() } })

						onDateChange(newEvent)
					}
				}

				return newState
			})
		}
	}

	isDateOutofBoundaries = (date: string | Date) => {
		const { minDate, maxDate } = this.props
		if (minDate && maxDate) {
			return moment(date).isBefore(minDate) || moment(date).isAfter(maxDate)
		}
		return false;
	}

	populateDate = (date: Date | string) => {
		const jsDate = new Date(date)
		const day = jsDate.getDate()
		const month = MONTHS[jsDate.getMonth()]
		const year = jsDate.getFullYear()

		this.setState((prevState: DatePickerState) => ({
			...prevState,
			day,
			month,
			year,
		}))
	}

	componentDidMount() {
		if (isIE || isSafari) {
			const { date } = this.props
			if (date) this.populateDate(date)
		}
	}

	componentWillReceiveProps(nextProps: DatePickerProps) {
    
		const { date } = nextProps
		if (R.equals(this.props.date, date)) return

		this.setState((prevState: DatePickerState) => {
			return {
				...prevState,
				localDate: date || '',
			}
		})
	}

	public render() {
		const { classes, fullSize, hideLabel, label, maxDate, minDate, style, rootStyle } = this.props
		const { day, month, year, dateError, localDate } = this.state
    
		return !isIE && !isSafari ? (
			<div className={`${classes.root} ${fullSize ? classes.full : ''}`}>
				<TextField
					fullWidth={true}
					required={this.props.required}
					label={hideLabel ? '' : label}
					type="date"
					value={localDate ? moment(localDate).format('YYYY-MM-DD') : ''}
					margin="dense"
					variant="outlined"
					className={`${classes.datePicker} ${fullSize ? classes.full : ''}`}
					onChange={this.handleChange}
					InputLabelProps={{
						shrink: true,
					}}
					InputProps={{
						inputProps: {
							max: maxDate || '2999-12-31',
							min: minDate || ''
						},
					}}
				/>
			</div>
		) : (
			<FormControl variant="outlined" className={`${classes.root} ${fullSize ? classes.full : ''}`} style={rootStyle}>
				{!hideLabel && <FormLabel component="label">{label}</FormLabel>}

				<div className={classes.formItems} data-auto="date-wrapper" style={style}>
					<TextField
						error={dateError}
						required={this.props.required}
						id="month"
						name="month"
						select={true}
						className={classes.month}
						value={month || ''}
						onChange={this.handleChange}
						SelectProps={{
							native: true,
							MenuProps: {
								className: classes.menu,
							},
						}}
						label="Month"
						margin="dense"
						variant="outlined"
					>
						{!month && <option />}
						{MONTHS.map((m: string) => (
							<option key={m} value={m}>
								{m}
							</option>
						))}
					</TextField>

					<TextField
						error={dateError}
						required={this.props.required}
						name="day"
						id="day"
						select={true}
						className={classes.day}
						value={day || ''}
						onChange={this.handleChange}
						SelectProps={{
							native: true,
							MenuProps: {
								className: classes.menu,
							},
						}}
						label="Day"
						margin="dense"
						variant="outlined"
					>
						{!day && <option />}
						{DAYS.map((option: number) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</TextField>

					<TextField
						error={dateError}
						required={this.props.required}
						id="year"
						name="year"
						select={true}
						className={classes.year}
						value={year || ''}
						onChange={this.handleChange}
						SelectProps={{
							native: true,
							MenuProps: {
								className: classes.menu,
							},
						}}
						label="Year"
						margin="dense"
						variant="outlined"
					>
						{!year && <option />}
						{this.YEARS.map((y: number) => (
							<option key={y} value={y}>
								{y}
							</option>
						))}
					</TextField>
				</div>
			</FormControl>
		)
	}
}

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flex: 1,
		},
		formItems: {
			display: 'flex',
			flex: 1,
			flexDirection: 'row',
			flexWrap: 'nowrap',
			justifyContent: 'space-between',
		},
		datePicker: {
			flexBasis: isIE || isSafari ? '100%' : '40%',

			[theme.breakpoints.down('sm')]: {
				flexBasis: '100%',
			},
		},
		day: {
			flexBasis: '24%',
		},
		month: {
			flexBasis: '45%',
		},
		year: {
			flexBasis: '28%',
		},
		menu: {},
		full: {
			flexBasis: '100%',
		},
	})

export default withStyles(styles)(DatePicker)
